import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { publicApi } from "./orderApi";
import { deviceId } from "./useCart";
import { loadWidget, sdkSendOtp, sdkRetryOtp, sdkVerifyOtp, widgetAccessToken, widgetErrorText, CAPTCHA_CONTAINER_ID } from "@/pages/public/qr/msg91Widget";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || (typeof d === "string" ? d : null))) || fb; };

/** OTP boxes backed by ONE real input (paste + one-time-code autofill friendly). Auto-verifies once the full code is in. */
export const OtpInput = ({ length, value, onChange, disabled, testId = "otp-code" }) => {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const digits = Array.from({ length }, (_, i) => value[i] || "");
  return (
    <div className="os-otp" style={{ "--n": length }} onClick={() => ref.current?.focus()} data-testid={`${testId}-boxes`}>
      {digits.map((d, i) => <i key={i} className={i === Math.min(value.length, length - 1) && !disabled ? "active" : ""} aria-hidden="true">{d}</i>)}
      <input ref={ref} value={value} disabled={disabled} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={length}
        aria-label={`${length}-digit OTP`} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, length))}
        onPaste={(e) => { const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length); if (t) { e.preventDefault(); onChange(t); } }} data-testid={testId} />
    </div>
  );
};

/**
 * MSG91 widget OTP flow (unchanged provider integration + CAPTCHA). onVerified(verification_token) is called once the backend
 * confirms the widget access token for THIS phone. Never places an order itself.
 */
export const OtpVerify = ({ slug, branchId, phone, otpWidget, onVerified, idPrefix = "otp" }) => {
  const length = otpWidget?.otp_length || 4;
  const [stage, setStage] = useState("idle"); // idle | sending | sent | verifying | verified
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const tried = useRef("");
  const inflight = useRef(false);

  useEffect(() => { if (cooldown > 0) { const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); } }, [cooldown]);
  useEffect(() => { if (otpWidget) loadWidget(otpWidget).catch(() => {}); }, [otpWidget]);
  useEffect(() => { setStage("idle"); setCode(""); setError(""); tried.current = ""; }, [phone]); // changing the number invalidates the old verification state

  const send = async () => {
    setError(""); setStage("sending");
    try {
      if (!otpWidget) throw new Error("not_configured");
      await loadWidget(otpWidget);
      await (stage === "sent" || tried.current ? sdkRetryOtp() : sdkSendOtp(`91${phone}`));
      setStage("sent"); setCooldown(30); setCode(""); tried.current = "";
    } catch (e) { setStage(tried.current || stage === "sent" ? "sent" : "idle"); setError(widgetErrorText(e, "Could not send OTP. Try again.")); }
  };

  const verify = async (c) => {
    if (inflight.current || tried.current === c) return; // no duplicate / repeated-same-code verification
    inflight.current = true; tried.current = c; setError(""); setStage("verifying");
    try {
      const access_token = widgetAccessToken(await sdkVerifyOtp(c));
      const r = await publicApi.post(`/${slug}/branches/${branchId}/otp/widget-verify`, { customer_phone: phone, device_id: deviceId(), access_token });
      setStage("verified");
      onVerified(r.data.verification_token);
    } catch (e) {
      const raw = widgetErrorText(e, "");
      const expired = /expir/i.test(raw) || /expir/i.test(errText(e, ""));
      setError(expired ? "This code has expired. Tap Resend to get a new one." : errText(e, raw || "Incorrect code. Check the SMS and try again."));
      setStage("sent");
    } finally { inflight.current = false; }
  };

  const onCode = (v) => { setCode(v); if (error) setError(""); if (v.length === length) verify(v); };

  if (stage === "verified") return <div className="os-ok" data-testid={`${idPrefix}-verified`}><ShieldCheck size={16} />Mobile verified</div>;
  return (
    <div data-testid={`${idPrefix}-block`}>
      <div id={CAPTCHA_CONTAINER_ID} style={{ marginBottom: 8 }} />
      {stage === "idle" || stage === "sending" ? (
        <button type="button" className="os-btn outline" onClick={send} disabled={stage === "sending" || !otpWidget} data-testid={`${idPrefix}-send`}>{stage === "sending" ? "Sending…" : "Send OTP"}</button>
      ) : (
        <>
          <p className="os-hint" style={{ margin: "0 0 6px", textAlign: "center" }}>Enter the {length}-digit code sent to +91 {phone}</p>
          <OtpInput length={length} value={code} onChange={onCode} disabled={stage === "verifying"} testId={`${idPrefix}-code`} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 36 }}>
            <span className="os-hint" style={{ margin: 0 }} data-testid={`${idPrefix}-state`}>
              {stage === "verifying" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Loader2 size={13} className="animate-spin" />Verifying…</span> : code.length < length ? "Code verifies automatically" : ""}
            </span>
            <button type="button" className="os-btn text" onClick={send} disabled={cooldown > 0 || stage === "verifying"} data-testid={`${idPrefix}-resend`}>{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
          </div>
        </>
      )}
      {error && <p className="os-err" role="alert" data-testid={`${idPrefix}-error`}>{error}</p>}
      {!otpWidget && <p className="os-err" data-testid={`${idPrefix}-not-configured`}>OTP verification is not configured for this outlet.</p>}
    </div>
  );
};
